import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import { testimonialsData, statsData } from '@/mocks/homeData';
import { getWebSiteSchema, getWebPageSchema, getBreadcrumbSchema } from '@/utils/schemas';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      form.reset();
      return;
    }

    setFormStatus('loading');
    setFormError('');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9rb25knlsngrm4lm9sg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        setFormError('提交失败，请稍后再试');
        setFormStatus('error');
        return;
      }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else if (serverMsg && (typeof serverMsg === 'string') && (serverMsg.includes('spam') || serverMsg.includes('form data is spam'))) {
        setFormError('提交失败，请稍后再试');
        setFormStatus('error');
      } else {
        setFormError(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后再试');
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请稍后再试');
      setFormStatus('error');
    }
  };

  const pageTitle = '关于惠美梨 - 品牌故事、口碑评价与联系方式 | 惠美梨产地直供';
  const pageDesc = '惠美梨三十年品质坚守，5000余亩自有果园，年销梨果超100万斤。了解惠美梨品牌故事、种植理念、技术实力与消费者口碑。联系我们获取产品咨询、团购合作服务。';

  const schemas = [
    getWebSiteSchema(),
    getWebPageSchema('关于惠美梨', pageDesc, 'https://hwsxsp.cn/about'),
    getBreadcrumbSchema([
      { name: '首页', url: 'https://hwsxsp.cn/' },
      { name: '关于我们', url: 'https://hwsxsp.cn/about' },
    ]),
  ];

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={pageDesc}
        canonicalUrl="https://hwsxsp.cn/about"
        schema={schemas}
      />
      <Navbar />
      <main className="pt-16 md:pt-20">
        <section className="w-full py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-2 text-sm text-foreground-400 mb-8" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-700">关于我们</span>
            </nav>
          </div>
        </section>

        <section id="story" className="w-full py-16 md:py-24 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row gap-10 md:gap-14 items-center">
              <div className="w-full lg:w-1/2">
                <div className="relative h-72 md:h-[450px] rounded-2xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Beautiful%20pear%20orchard%20in%20golden%20sunlight%20with%20ripe%20pears%20on%20trees%2C%20rolling%20hills%20in%20background%2C%20morning%20mist%2C%20pastoral%20landscape%2C%20warm%20natural%20light%2C%20artistic%20photography%2C%20serene%20agricultural%20scene&width=800&height=900&seq=about-orchard&orientation=portrait"
                    alt="惠美梨果园"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <div className="w-full lg:w-1/2">
                <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-4">
                  品牌故事
                </span>
                <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-6">
                  惠美梨——三十年品质坚守
                </h1>
                <div className="space-y-4 text-foreground-600 leading-relaxed">
                  <p>
                    惠美梨的故事始于三十年前，创始人怀揣着"让更多人吃到真正好梨"的朴素愿望，在优质梨果核心产区扎根。从最初的几十亩试验田，到如今占地5000余亩的现代化自有果园，惠美梨始终秉持一个信念——<strong>品质是唯一的通行证</strong>。
                  </p>
                  <p>
                    我们深知，一颗好梨的背后，是天时、地利、人和的共同作用。惠美梨的果园位于北纬30°黄金水果种植带，这里气候温和、光照充足、昼夜温差大，是大自然赐予的梨果天堂。清澈的山泉水灌溉着每一棵梨树，肥沃的土壤滋养着每一颗果实。
                  </p>
                  <p>
                    三十年来，惠美梨不断引进先进种植技术和管理理念，从传统农耕到智慧农业，从人工分选到AI视觉检测，我们从未停止对品质的追求。如今，惠美梨已成为集种植、分选、仓储、销售于一体的现代化梨果品牌，年销梨果超过100万斤，产品远销全国各地。
                  </p>
                  <p>
                    未来，惠美梨将继续深耕品质之路，让更多人品尝到来自核心产区的纯正梨果风味。我们相信，<strong>好吃的梨，值得被更多人知道</strong>。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full py-16 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
              {statsData.map((stat, index) => (
                <div key={index} className="text-center p-6 rounded-2xl bg-background-50">
                  <div className="font-heading text-3xl md:text-4xl font-bold text-primary-500 mb-2">
                    {stat.value}
                  </div>
                  <div className="text-sm text-foreground-500 font-medium">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="testimonials" className="w-full py-16 md:py-24 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12 md:mb-16">
              <span className="inline-block px-4 py-1.5 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium mb-4">
                顾客口碑
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-4">
                品质，由消费者来评价
              </h2>
              <p className="text-foreground-500 text-sm md:text-base max-w-2xl mx-auto">
                每一句好评，都是对惠美梨三十年品质坚守的最好认可
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              {testimonialsData.map((testimonial, index) => (
                <div key={index} className="p-6 md:p-8 rounded-2xl bg-background-100">
                  <div className="flex items-center gap-1 mb-4">
                    {Array.from({ length: testimonial.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                    "{testimonial.content}"
                  </p>
                  <div className="flex items-center gap-3 pt-4 border-t border-background-200/70">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                      <span className="font-heading text-primary-600 font-semibold text-sm">
                        {testimonial.name.charAt(0)}
                      </span>
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-foreground-900">{testimonial.name}</div>
                      <div className="text-xs text-foreground-500">{testimonial.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="contact" className="w-full py-16 md:py-24 bg-background-100">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10 md:mb-14">
              <span className="inline-block px-4 py-1.5 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-4">
                联系我们
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-4">
                期待与您联系
              </h2>
              <p className="text-foreground-500 text-sm md:text-base">
                无论是产品咨询、团购合作还是任何疑问，欢迎随时联系我们
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
              <div className="p-6 rounded-2xl bg-background-50 text-center">
                <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600">
                  <i className="ri-phone-line text-xl"></i>
                </div>
                <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-2">客服热线</h3>
                <p className="text-sm text-foreground-500">400-XXX-XXXX</p>
                <p className="text-xs text-foreground-400 mt-1">工作日 9:00-18:00</p>
              </div>
              <div className="p-6 rounded-2xl bg-background-50 text-center">
                <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600">
                  <i className="ri-mail-line text-xl"></i>
                </div>
                <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-2">电子邮箱</h3>
                <p className="text-sm text-foreground-500">service@hwsxsp.cn</p>
                <p className="text-xs text-foreground-400 mt-1">24小时内回复</p>
              </div>
              <div className="p-6 rounded-2xl bg-background-50 text-center">
                <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-xl bg-secondary-100 text-secondary-600">
                  <i className="ri-map-pin-line text-xl"></i>
                </div>
                <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-2">公司地址</h3>
                <p className="text-sm text-foreground-500">中国优质梨果核心产区</p>
                <p className="text-xs text-foreground-400 mt-1">惠美梨自有果园基地</p>
              </div>
            </div>

            <form
              onSubmit={handleSubmit}
              data-readdy-form
              className="p-6 md:p-8 rounded-2xl bg-background-50"
            >
              <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-6">在线留言</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-2">
                    姓名 <span className="text-accent-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="contact-name"
                    name="name"
                    required
                    className="w-full px-4 py-3 rounded-lg bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-2">
                    邮箱 <span className="text-accent-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="contact-email"
                    name="email"
                    required
                    className="w-full px-4 py-3 rounded-lg bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入您的邮箱"
                  />
                </div>
              </div>

              <div className="mb-4">
                <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-2">
                  咨询类型
                </label>
                <select
                  id="contact-subject"
                  name="subject"
                  className="w-full px-4 py-3 rounded-lg bg-background-100 border border-background-200/70 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors cursor-pointer"
                >
                  <option value="">请选择咨询类型</option>
                  <option value="product">产品咨询</option>
                  <option value="order">订单查询</option>
                  <option value="wholesale">团购合作</option>
                  <option value="partnership">商务合作</option>
                  <option value="other">其他</option>
                </select>
              </div>

              <div className="mb-4">
                <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-2">
                  留言内容 <span className="text-accent-500">*</span>
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  maxLength={500}
                  rows={4}
                  className="w-full px-4 py-3 rounded-lg bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                  placeholder="请输入您的留言内容（500字以内）"
                ></textarea>
                <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
              </div>

              <div className="spam-field">
                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
              </div>

              <button
                type="submit"
                disabled={formStatus === 'loading'}
                className="w-full px-8 py-3.5 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {formStatus === 'loading' ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    提交中...
                  </>
                ) : (
                  '提交留言'
                )}
              </button>

              {formStatus === 'success' && (
                <div className="mt-4 p-4 rounded-lg bg-green-50 text-green-700 text-sm text-center">
                  <i className="ri-checkbox-circle-line mr-1"></i>
                  留言提交成功！我们会尽快与您联系。
                </div>
              )}

              {formStatus === 'error' && formError && (
                <div className="mt-4 p-4 rounded-lg bg-red-50 text-red-700 text-sm text-center">
                  <i className="ri-error-warning-line mr-1"></i>
                  {formError}
                </div>
              )}
            </form>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}